import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFamily } from "@/hooks/useFamily";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { generateInviteCode } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { ArrowLeft, Copy, Mail, Link as LinkIcon, Check } from "lucide-react";

export default function InviteCoparent() {
  const { user } = useAuth();
  const { familyId } = useFamily();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const preselectedChildId = searchParams.get("child");
  const [selectedChildIds, setSelectedChildIds] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Fetch children
  const { data: children = [] } = useQuery({
    queryKey: ["children", familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .eq("family_id", familyId!)
        .order("created_at");
      if (error) throw error;
      return data;
    },
    enabled: !!familyId,
  });

  // Fetch existing coparents
  const { data: existingCoparents = [] } = useQuery({
    queryKey: ["child-coparents-all", familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("child_coparents" as any)
        .select("child_id, coparent_user_id, display_name")
        .eq("family_id", familyId!);
      if (error) throw error;
      return data as unknown as { child_id: string; coparent_user_id: string; display_name: string | null }[];
    },
    enabled: !!familyId,
  });

  // Children that already have a coparent
  const childrenWithCoparent = useMemo(
    () => new Set(existingCoparents.map((c) => c.child_id)),
    [existingCoparents]
  );

  // Available children (no coparent yet)
  const availableChildren = useMemo(
    () => children.filter((c) => !childrenWithCoparent.has(c.id)),
    [children, childrenWithCoparent]
  );

  // Pre-select children on first load
  if (!initialized && availableChildren.length > 0) {
    setInitialized(true);
    if (preselectedChildId && availableChildren.some((c) => c.id === preselectedChildId)) {
      setSelectedChildIds(new Set([preselectedChildId]));
    } else {
      // Pre-select all available children by default
      setSelectedChildIds(new Set(availableChildren.map((c) => c.id)));
    }
  }

  const toggleChild = (childId: string) => {
    setSelectedChildIds((prev) => {
      const next = new Set(prev);
      if (next.has(childId)) next.delete(childId);
      else next.add(childId);
      return next;
    });
  };

  // Generate invite link
  const createInvite = useMutation({
    mutationFn: async () => {
      if (!user?.id || !familyId) throw new Error("Missing data");
      const code = generateInviteCode();
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      const childIdsArray = Array.from(selectedChildIds);
      const { error } = await supabase.from("family_invites").insert({
        family_id: familyId,
        created_by: user.id,
        invite_code: code,
        expires_at: expiresAt,
        child_ids: childIdsArray,
      } as any);
      if (error) throw error;

      // Enable custody for selected children
      await enableCustodyForSelected();

      return `${window.location.origin}/invite/${code}`;
    },
    onSuccess: (link) => {
      setInviteLink(link);
      toast.success("Invitasjonslenke opprettet!", {
        description: "Del lenken med medforelderen. Den er gyldig i 7 dager.",
      });
    },
    onError: () => toast.error("Kunne ikke opprette invitasjonslenke"),
  });

  // Send email invite
  const sendEmail = useMutation({
    mutationFn: async () => {
      let link = inviteLink;
      if (!link) {
        link = await createInvite.mutateAsync();
      }
      const { error } = await supabase.functions.invoke("send-invite-email", {
        body: { email: inviteEmail.trim(), inviteLink: link },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(`Invitasjon sendt til ${inviteEmail}! ✉️`, {
        description: "Medforelderen får en e-post med lenke for å bli med i familien.",
        duration: 5000,
      });
      setInviteEmail("");
      setEmailSent(true);
    },
    onError: () => toast.error("Kunne ikke sende e-post"),
  });

  async function enableCustodyForSelected() {
    const childrenToEnable = children.filter(
      (c) => selectedChildIds.has(c.id) && !c.custody_enabled
    );
    for (const child of childrenToEnable) {
      await supabase
        .from("children")
        .update({ custody_enabled: true } as any)
        .eq("id", child.id);
    }
    if (childrenToEnable.length > 0) {
      queryClient.invalidateQueries({ queryKey: ["children"] });
      queryClient.invalidateQueries({ queryKey: ["all-children"] });
    }
  }

  const handleCopyLink = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    setLinkCopied(true);
    toast.success("Lenke kopiert!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const hasSelection = selectedChildIds.size > 0;

  return (
    <div className="min-h-full bg-background px-4 pt-6 pb-6">
      <div className="mx-auto max-w-sm space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate("/settings")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="font-heading text-lg font-semibold tracking-tight">
            Legg til medforelder
          </h1>
        </div>

        {/* Step 1: Select children */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Velg barn</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Velg hvilke barn invitasjonen skal gjelde for.
            </p>

            {availableChildren.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                Alle barn har allerede en medforelder eller ventende invitasjon.
              </p>
            ) : (
              <div className="space-y-1.5">
                {availableChildren.map((child) => (
                  <label
                    key={child.id}
                    className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    <Checkbox
                      checked={selectedChildIds.has(child.id)}
                      onCheckedChange={() => toggleChild(child.id)}
                    />
                    <span className="text-xl">{child.avatar_emoji}</span>
                    <span className="text-sm font-medium">{child.name}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Show children that already have coparents */}
            {children.filter((c) => childrenWithCoparent.has(c.id)).length > 0 && (
              <>
                <Separator className="my-3" />
                <p className="text-xs text-muted-foreground font-medium">Har allerede medforelder:</p>
                {children
                  .filter((c) => childrenWithCoparent.has(c.id))
                  .map((child) => (
                    <div
                      key={child.id}
                      className="flex items-center gap-3 rounded-lg border border-dashed p-3 opacity-60"
                    >
                      <span className="text-xl">{child.avatar_emoji}</span>
                      <span className="text-sm font-medium">{child.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">✓ Koblet</span>
                    </div>
                  ))}
              </>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Generate link or send email */}
        {hasSelection && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Inviter</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {inviteLink ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Input
                      value={inviteLink}
                      readOnly
                      className="text-xs flex-1"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCopyLink}
                      className="gap-1.5"
                    >
                      {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {linkCopied ? "Kopiert" : "Kopier"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Lenken er gyldig i 7 dager.
                  </p>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => createInvite.mutate()}
                  disabled={createInvite.isPending}
                >
                  <LinkIcon className="h-4 w-4" />
                  {createInvite.isPending ? "Oppretter…" : "Lag invitasjonslenke"}
                </Button>
              )}

              <Separator />

              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Eller send på e-post:</p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="E-postadresse"
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && inviteEmail.trim()) sendEmail.mutate();
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={() => sendEmail.mutate()}
                    disabled={!inviteEmail.trim() || sendEmail.isPending}
                    className="gap-1.5"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    {sendEmail.isPending ? "…" : "Send"}
                  </Button>
                </div>
              </div>

              {emailSent && (
                <div className="rounded-lg border border-green-200 bg-green-50/50 p-3 flex items-start gap-2">
                  <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">E-post sendt!</p>
                    <p className="text-xs text-green-700/70">
                      Medforelderen vil motta en e-post med invitasjonslenke. Du kan sjekke status på innstillingssiden.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

    </div>
  );
}
